/* A/B test: mobile-menu challenger drawer. Spec: docs/ab-tests/2026-09-18-mobile-menu.md */
class AbMobileMenu extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('.ab-mm__toggle');
    this.panel = this.querySelector('.ab-mm__panel');
    this.screens = this.querySelector('.ab-mm__screens');
    this.isOpen = false;
    this.current = 'main';

    this.onToggle = () => (this.isOpen ? this.close() : this.open());
    this.onKeydown = (evt) => { if (evt.key === 'Escape' && this.isOpen) this.close(); };
    this.onClick = this.handleClick.bind(this);
    this.onChange = this.handleChange.bind(this);
    this.desktopQuery = window.matchMedia(window.theme?.mediaQueries?.md || '(min-width: 769px)');
    this.onMediaChange = (evt) => { if (evt.matches && this.isOpen) this.close(); };

    this.toggle.addEventListener('click', this.onToggle);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.desktopQuery.addEventListener('change', this.onMediaChange);
  }

  disconnectedCallback() {
    this.toggle.removeEventListener('click', this.onToggle);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    this.desktopQuery.removeEventListener('change', this.onMediaChange);
    if (this.isOpen) this.close();
  }

  open() {
    this.isOpen = true;
    this.classList.add('is-open');
    this.toggle.setAttribute('aria-expanded', 'true');
    this.showScreen('main', 'none');
    if (typeof trapFocus === 'function') trapFocus(this.panel, this.panel);
    this.emit('on:ab-mm:open');
  }

  close() {
    this.isOpen = false;
    this.classList.remove('is-open');
    this.toggle.setAttribute('aria-expanded', 'false');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.toggle);
    this.emit('on:ab-mm:close');
  }

  showScreen(id, direction = 'forward', moveFocus = false) {
    const next = this.screens.querySelector(`.ab-mm__screen[data-screen="${id}"]`);
    if (!next) return;
    const prev = this.screens.querySelector('.ab-mm__screen:not([hidden])');
    if (prev && prev !== next) prev.hidden = true;

    next.hidden = false;
    next.classList.remove('is-entering', 'is-entering-back');
    if (direction === 'forward') next.classList.add('is-entering');
    if (direction === 'back') next.classList.add('is-entering-back');
    next.addEventListener('animationend', () => {
      next.classList.remove('is-entering', 'is-entering-back');
      next.querySelectorAll('carousel-slider').forEach((rail) => {
        if (rail.slider) rail.handleBreakpointChange();
      });
    }, { once: true });

    this.panel.scrollTop = 0;
    this.current = id;

    if (moveFocus) {
      const focusTarget = next.querySelector('.ab-mm__back') || next.querySelector('a, button');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }

    this.emit('on:ab-mm:screen', { screen: id, label: next.dataset.mmScreenLabel || id });
  }

  handleClick(evt) {
    const opener = evt.target.closest('[data-mm-open]');
    if (opener) {
      evt.preventDefault();
      this.showScreen(opener.dataset.mmOpen, 'forward', evt.detail === 0);
      return;
    }

    const back = evt.target.closest('[data-mm-back]');
    if (back) {
      evt.preventDefault();
      this.showScreen(back.dataset.mmBack || 'main', 'back', evt.detail === 0);
      return;
    }

    const summary = evt.target.closest('summary.ab-mm__acc-head');
    if (summary && summary.parentElement.querySelector('.ab-mm__acc-body')) {
      evt.preventDefault();
      this.toggleAccordion(summary.parentElement);
      return;
    }

    const link = evt.target.closest('a[href^="#"]');
    if (link) this.close();
  }

  handleChange(evt) {
    const select = evt.target.closest('[data-mm-currency]');
    if (!select || !select.form) return;
    const country = select.selectedOptions[0]?.dataset.country;
    if (country) select.form.elements.country_code.value = country;
    select.form.submit();
  }

  toggleAccordion(details) {
    if (details.classList.contains('is-animating')) return;
    const body = details.querySelector('.ab-mm__acc-body');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timing = { duration: reduce ? 0 : 260, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' };
    details.classList.add('is-animating');

    if (details.open) {
      details.classList.add('is-closing');
      const from = body.getBoundingClientRect().height;
      const anim = body.animate([{ height: `${from}px`, opacity: 1 }, { height: '0px', opacity: 0 }], timing);
      anim.onfinish = () => {
        details.open = false;
        details.classList.remove('is-closing', 'is-animating');
      };
    } else {
      details.open = true;
      const to = body.getBoundingClientRect().height;
      const anim = body.animate([{ height: '0px', opacity: 0 }, { height: `${to}px`, opacity: 1 }], timing);
      anim.onfinish = () => details.classList.remove('is-animating');
    }
  }

  emit(name, detail = {}) {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  }
}

if (!customElements.get('ab-mobile-menu')) {
  customElements.define('ab-mobile-menu', AbMobileMenu);
}
